import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import _ from 'lodash';
import { EmailTemplateType, IAction, IEmailTemplate, IValidationErrorCollection } from '../models';
import { Status } from '../models/Status';
import * as EmailTemplateAPI from '../services/EmailTemplateAPI';

const defaultReminderEmail: IEmailTemplate = {
	emailTemplateId: 1,
	audienceId: 1,
	invitationType: EmailTemplateType.Reminder,
	subject: '',
	content: '',
	action: IAction.None,
};
const defaultEmailTemplate: IEmailTemplate = {
	emailTemplateId: 0,
	emailTemplateName: '',
	audienceId: 0,
	invitationType: EmailTemplateType.Invitation,
	from: '',
	replyToEmail: '',
	subject: '',
	content: '',
	createdOn: undefined,
	uniqueId: '1',
	action: IAction.None,
	enableHtmlContent: false,
};

interface IEmailTemplateState {
	status: Status;
	invitationEmail: IEmailTemplate;
	isEmailTemplateLoading: boolean;
	reminderEmails: IEmailTemplate[];
	defaultEmailTemplates: IEmailTemplate[];
	invitationEmailValidationErrors?: IValidationErrorCollection;
	reminderEmailValidationErrors?: IValidationErrorCollection;
}

const initialState: IEmailTemplateState = {
	status: Status.Idle,
	invitationEmail: defaultEmailTemplate,
	isEmailTemplateLoading: false,
	reminderEmails: [],
	defaultEmailTemplates: [],
};

const shapeErrorResponse = (error: unknown) => {
	if (axios.isAxiosError(error) && error.response && error.response.data && error.response.data.validationErrors) {
		return error.response.data.validationErrors.validations;
	} else {
		return error;
	}
};

const getEmailTemplates = createAsyncThunk(
	'emailTemplate/getEmailTemplateList',
	async ({ surveyId, audienceId, emailTemplateType }: { surveyId: number; audienceId: number; emailTemplateType?: EmailTemplateType }) => {
		return EmailTemplateAPI.getEmailTemplates(surveyId, audienceId, emailTemplateType);
	},
);

const addEmailTemplate = createAsyncThunk(
	'emailTemplate/addEmailTemplate',
	async ({ surveyId, emailTemplate }: { surveyId: number; emailTemplate: IEmailTemplate }, { rejectWithValue }) => {
		try {
			const template = await EmailTemplateAPI.addEmailTemplate(surveyId, emailTemplate);
			return { data: template };
		} catch (ex: unknown) {
			const validationError = shapeErrorResponse(ex);
			return rejectWithValue({ data: emailTemplate, error: validationError });
		}
	},
);

const updateEmailTemplate = createAsyncThunk(
	'emailTemplate/updateEmailTemplate',
	async ({ surveyId, emailTemplate }: { surveyId: number; emailTemplate: IEmailTemplate }, { rejectWithValue }) => {
		try {
			const template = await EmailTemplateAPI.updateEmailTemplate(surveyId, emailTemplate);
			return { data: template };
		} catch (ex: unknown) {
			const validationError = shapeErrorResponse(ex);
			return rejectWithValue({ data: emailTemplate, error: validationError });
		}
	},
);

const deleteEmailTemplate = createAsyncThunk(
	'emailTemplate/deleteEmailTemplate',
	async ({ surveyId, audienceId, emailTemplateId }: { surveyId: number; audienceId: number; emailTemplateId: number }) =>
		EmailTemplateAPI.deleteEmailTemplate(surveyId, audienceId, emailTemplateId),
);

const archiveAll = createAsyncThunk(
	'emailTemplate/archiveAllTemplatesForAudience',
	async ({ surveyId, audienceId }: { surveyId: number; audienceId: number }) =>
		EmailTemplateAPI.archiveAudienceEmailTemplates(surveyId, audienceId),
);

const getDefaultEmailTemplateList = createAsyncThunk('emailTemplate/getDefaultEmailTemplateList', async () =>
	EmailTemplateAPI.getEmailTemplateListFromQuestionLibrary(),
);

const emailTemplateSlice = createSlice({
	name: 'emailTemplateSlice',
	initialState: initialState,
	reducers: {
		resetEmailTemplates: () => {
			return { ...initialState };
		},
		addReminderToList: (state, { payload: { emailTemplate } }: PayloadAction<{ emailTemplate: IEmailTemplate }>) => {
			let updatedEmailTemplates = state.reminderEmails;
			const index = _.findIndex(state.reminderEmails, { uniqueId: emailTemplate.uniqueId });
			emailTemplate.action = IAction.Insert;

			if (index === -1) {
				updatedEmailTemplates = [...state.reminderEmails, emailTemplate];
			} else {
				if (emailTemplate.emailTemplateId !== 0) {
					emailTemplate.action = IAction.Update;
				}
				updatedEmailTemplates.splice(index, 1, { ...emailTemplate });
			}
			state.reminderEmails = updatedEmailTemplates;
		},
		updateInvitationEmail: (state, { payload: { invitationEmail } }: PayloadAction<{ invitationEmail: IEmailTemplate }>) => {
			invitationEmail.action = invitationEmail.emailTemplateId !== 0 ? IAction.Update : IAction.Insert;

			let reminderEmails = state.reminderEmails;
			if (reminderEmails) {
				reminderEmails = reminderEmails.map((reminderEmail) => {
					reminderEmail.from = invitationEmail.from;
					reminderEmail.replyToEmail = invitationEmail.replyToEmail;
					reminderEmail.action = reminderEmail.emailTemplateId !== 0 ? IAction.Update : IAction.Insert;
					return reminderEmail;
				});
			}
			state.invitationEmail = invitationEmail;
			state.reminderEmails = reminderEmails;
		},
		deleteReminder: (state, action: PayloadAction<IEmailTemplate>) => {
			const updatedEmailTemplates: IEmailTemplate[] = state.reminderEmails;
			action.payload.action = IAction.Delete;
			const index = _.findIndex(state.reminderEmails, { uniqueId: action.payload.uniqueId });
			updatedEmailTemplates.splice(index, 1, action.payload);
			state.reminderEmails = updatedEmailTemplates;
		},
		addInvitationEmailValidationErrors: (state, action: PayloadAction<IValidationErrorCollection | undefined>) => {
			state.invitationEmailValidationErrors = action.payload;
		},
		addReminderEmailValidationErrors: (state, action: PayloadAction<IValidationErrorCollection | undefined>) => {
			state.reminderEmailValidationErrors = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getDefaultEmailTemplateList.fulfilled, (state, action) => {
				const templates = action.payload.map((t: any) => {
					return { ...t, emailTemplateName: t.name };
				});
				state.defaultEmailTemplates = templates;
				state.status = Status.Idle;
			})
			.addCase(getEmailTemplates.fulfilled, (state, action) => {
				const invitationEmail =
					_.find(action.payload, { invitationType: EmailTemplateType.Invitation }) || _.cloneDeep(defaultEmailTemplate);
				invitationEmail.action = IAction.None;
				let reminderEmails = _.filter(action.payload, { invitationType: EmailTemplateType.Reminder });
				reminderEmails = reminderEmails.map((et) => {
					et.uniqueId = _.uniqueId();
					et.action = IAction.None;
					return et;
				});
				state.invitationEmail = invitationEmail;
				state.reminderEmails = reminderEmails;
				state.status = Status.Idle;
			})
			.addCase(getEmailTemplates.rejected, (state) => {
				state.invitationEmail = _.cloneDeep(defaultEmailTemplate);
				state.reminderEmails = [];
				state.status = Status.Idle;
			})
			.addCase(deleteEmailTemplate.pending, (state) => {
				state.status = Status.Deleting;
			})
			.addCase(addEmailTemplate.fulfilled, (state, action) => {
				const addedTemplate = action.payload.data;
				if (addedTemplate.invitationType === EmailTemplateType.Reminder) {
					state.reminderEmails.push(addedTemplate);
				}
				state.status = Status.Idle;
			})
			.addMatcher(isAnyOf(addEmailTemplate.pending, updateEmailTemplate.pending), (state) => {
				state.status = Status.Saving;
			})
			.addMatcher(
				isAnyOf(
					updateEmailTemplate.fulfilled,
					deleteEmailTemplate.fulfilled,
					deleteEmailTemplate.rejected,
					getDefaultEmailTemplateList.rejected,
				),
				(state) => {
					state.status = Status.Idle;
				},
			)
			.addMatcher(isAnyOf(addEmailTemplate.rejected, updateEmailTemplate.rejected), (state, action) => {
				const { data, error } = action.payload as { data: IEmailTemplate; error: any };
				if (data.invitationType === EmailTemplateType.Reminder) {
					state.reminderEmailValidationErrors = error;
				} else {
					state.invitationEmailValidationErrors = error;
				}
				state.status = Status.Idle;
			})
			.addMatcher(isAnyOf(getEmailTemplates.pending, getDefaultEmailTemplateList.pending), (state) => {
				state.status = Status.Loading;
			});
	},
});

export {
	addEmailTemplate,
	archiveAll,
	defaultEmailTemplate,
	defaultReminderEmail,
	deleteEmailTemplate,
	getDefaultEmailTemplateList,
	getEmailTemplates,
	type IEmailTemplateState,
	initialState,
	updateEmailTemplate,
};
export const {
	addInvitationEmailValidationErrors,
	addReminderEmailValidationErrors,
	addReminderToList,
	deleteReminder,
	resetEmailTemplates,
	updateInvitationEmail,
} = emailTemplateSlice.actions;
export default emailTemplateSlice.reducer;
